import React, { useEffect, useMemo } from "react";

import { Typography, WorkerIcon, AutoscalingIcon } from "@packages/ds";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import styled, { withTheme, css as styledCSS } from "styled-components";

import { PlainServiceIcon } from "Components/ServiceIcon";
import { getServiceIcon } from "Components/ServiceIcon/helper";
import * as Dropdown from "ds/Dropdown";
import { Tooltip } from "ds/Tooltip";
import { semiBoldAlias } from "Libs/theme";
import { capitalize } from "Libs/utils";
import { selectFeatureAutoscalingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { useAppSelector } from "Store/hooks";

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const TitleHead = styled(Typography.Title)`
  margin-bottom: 11px;
`;

const BodyText = styled(Typography.BodyText)`
  margin-left: 5px;
  font-size: inherit;
  display: inline;
`;

const AutoscalingFlag = styled(AutoscalingIcon)`
  position: absolute;
  right: 20px;
`;

const getThemeAttributes = (theme, selected) => {
  if (theme.name === "dark") {
    return selected
      ? `color:var(--button-color-primary-600-color,var(--color-primary-600));
        box-shadow: 0px 8px 24px #1A192B;`
      : "color: #98A0AB";
  }

  return selected
    ? `color:var(--button-night-color,var(--night));
      box-shadow: 0px 6px 24px rgba(152, 160, 171, 0.4);`
    : "color: rgba(0, 0, 0, 0.7);";
};

const Block = styled.button.attrs({
  type: "div",
  tabIndex: 0
})`
  border: none;
  font-family: inherit;
  display: flex;
  flex-direction: column;
  font-size: 15px;
  justify-content: space-between;
  align-items: start;
  background-color: var(--section-snow-background-color, var(--snow));
  margin-bottom: 8px;
  border-radius: 8px;
  min-height: 48px;
  max-height: ${props => (props.hasWorkers ? "271px" : "48px")};
  min-width: 316px;
  ${props => getThemeAttributes(props.theme, props.selected)};

  &:hover {
    background-color: var(--button-snow-background-color, var(--snow));
    box-shadow: 0 4px 8px
      ${props =>
        props.theme.name == "dark" ? "#1A192B" : "rgba(152, 160, 171, 0.4)"};
  }

  ${({ selected }) =>
    selected &&
    styledCSS`
      ${Name} {
        color: var(--button-color-primary-600-color,var(--color-primary-600));
      }
      background-color: var(--button-snow-background-color,var(--snow));
      box-shadow: 0 4px 8px
        ${props =>
          props.theme.name == "dark" ? "#1A192B" : "rgba(152, 160, 171, 0.4)"};
    `};
`;

const Column = styled.div.attrs({ type: "div", tabIndex: 0 })`
  border: none;
  border-top: 1px solid var(--mode-border-neutral-weakest);
  font-family: inherit;
  display: flex;
  flex-direction: column;
  font-size: 15px;
  justify-content: space-between;
  align-items: start;
  background-color: var(--section-snow-background-color, var(--snow));
  padding: 12px 0 12px 16px;
  width: 100%;
  box-sizing: border-box;
  max-height: 200px;
  background-color: transparent;
  ${props => getThemeAttributes(props.theme, props.selected)};
`;

const Select = styled.div.attrs({ type: "div", tabIndex: 0 })`
  border: none;
  font-family: inherit;
  display: flex;
  flex-direction: column;
  font-size: 15px;
  justify-content: space-between;
  align-items: start;
  background-color: transparent;
  margin-top: 10px;
  overflow-y: auto;
  width: 100%;

  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

  ${props => getThemeAttributes(props.theme, props.selected)};
`;

const BlockLeftLayout = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: 100%;
  width: 100%;
  cursor: pointer;
  padding: 12px 16px;
  box-sizing: border-box;
  position: relative;
`;

const Name = styled.span`
  ${semiBoldAlias()};
  margin-left: 16px;
`;

const SubNameContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
`;

const SubName = styled.div`
  ${semiBoldAlias()};
  font-size: 13px;
`;

const SelectRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 80%;
  margin: 0 0 8px 32px;
  cursor: pointer;

  ${({ selected }) =>
    selected &&
    styledCSS`
      color: var(--button-color-primary-600-color,var(--color-primary-600));
    `}
`;

const SelectText = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: 1;
`;

const WorkerBadge = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const addClass = (list = {}, newClass) => {
  return Object.keys(list).reduce((current, key) => {
    current[key] = { class: newClass, ...list[key] };
    return current;
  }, {});
};

const ServicesList = ({
  onClick,
  currentDeployment,
  search,
  selected,
  autoscalingConfig
}) => {
  const intl = useIntl();

  const autoscalingEnabledFlag = useAppSelector(
    selectFeatureAutoscalingEnabled
  );

  const [activeTab, setActivetab] = React.useState(selected);
  const [openIndex, setOpenIndex] = React.useState(null);
  const nodeRouter = useMemo(() => {
    return {
      routes: { class: "router", ...(currentDeployment?.routes || {}) }
    };
  }, [currentDeployment?.routes]);
  const nodesApps = useMemo(() => {
    return {
      ...addClass(currentDeployment?.webapps, "app")
    };
  }, [currentDeployment?.webapps]);
  const nodesServices = useMemo(() => {
    return {
      ...addClass(currentDeployment?.services, "service")
    };
  }, [currentDeployment?.services]);

  useEffect(() => {
    if (selected) {
      setActivetab(selected);
    }
  }, [selected]);
  const workers = currentDeployment?.workers || {};

  // Match workers to their apps
  const nodesAppsWithWorkers = useMemo(() => {
    const result = structuredClone(nodesApps);
    for (const [workerName, workerData] of Object.entries(workers)) {
      const appKey = workerData.app;
      if (result[appKey]) {
        if (!result[appKey].workers) {
          result[appKey].workers = {};
        }
        const prefix = `${workers[workerName].app}--`;
        result[appKey].workers[workerName] = {
          ...workerData,
          displayName: workers[workerName].name.replace(prefix, "")
        };
      }
    }
    return result;
  }, [nodesApps, workers]);

  const workersTitle = intl.formatMessage({ id: "workers" });
  const capitalizedWorkersTitle = capitalize(workersTitle);
  const capitalizedServicesTitle = capitalize(
    intl.formatMessage({ id: "services" })
  );

  const toggleWorkersDropdown = (e, index) => {
    setOpenIndex(prev => (prev === index ? null : index));
    e?.stopPropagation();
  };

  const filterNodeCallback = node => {
    return Object.keys(node).filter(k =>
      search && search.length
        ? k.toUpperCase().startsWith(search.toUpperCase())
        : true
    );
  };

  const routerNodes = filterNodeCallback(nodeRouter);
  const appNodes = filterNodeCallback(nodesAppsWithWorkers);
  const serviceNodes = filterNodeCallback(nodesServices);

  return (
    <Layout data-testid="services-list">
      {routerNodes.map((k, i) => {
        const node = nodeRouter[k];
        node.name = k;

        const iconName = node?.type ? getServiceIcon(node).iconName : "router";
        return (
          <Block
            key={i}
            selected={activeTab === k}
            onClick={() => {
              onClick({
                ...node,
                iconName,
                class: node.class,
                metadata: {
                  appName: k
                }
              });
              setActivetab(k);
            }}
          >
            <BlockLeftLayout>
              <PlainServiceIcon
                service={node}
                iconName={iconName}
                alt={`${iconName}-icon`}
              />
              <Name>{node.name}</Name>
            </BlockLeftLayout>
          </Block>
        );
      })}

      <TitleHead tag="h4">
        {intl.formatMessage({
          id: "apps"
        })}
        <BodyText size="medium" $color="granite">
          {`(${Object.keys(nodesAppsWithWorkers).length})`}
        </BodyText>
      </TitleHead>

      {appNodes.map((k, index) => {
        const node = nodesAppsWithWorkers[k];
        node.name = k;
        const hasAutoscaling =
          autoscalingConfig?.autoscalingConfiguration?.services?.[k]?.enabled;
        const iconName = node?.type ? getServiceIcon(node).iconName : "router";
        return (
          <Block
            key={index}
            selected={activeTab === k}
            hasWorkers={node.workers}
          >
            <BlockLeftLayout
              onClick={() => {
                onClick({
                  ...node,
                  iconName,
                  class: node.class,
                  metadata: {
                    appName: k
                  }
                });
                setActivetab(k);
              }}
            >
              <PlainServiceIcon
                service={node}
                iconName={iconName}
                alt={`${iconName}-icon`}
              />
              {hasAutoscaling && autoscalingEnabledFlag && <AutoscalingFlag />}
              <Name>{node.name}</Name>
            </BlockLeftLayout>
            {node.workers ? (
              <Column>
                <SubNameContainer
                  onClick={e => toggleWorkersDropdown(e, index)}
                >
                  <SubName>
                    {capitalizedWorkersTitle}
                    <BodyText size="medium" $color="granite">
                      {`(${Object.keys(node.workers).length})`}
                    </BodyText>
                  </SubName>
                  <Dropdown.Chevron
                    direction={openIndex === index ? "up" : "down"}
                    fill="var(--mode-vector-neutral-default)"
                  />
                </SubNameContainer>
                {openIndex === index ? (
                  <Select>
                    {Object.values(node.workers).map((worker, k) => (
                      <SelectRow
                        key={k}
                        selected={activeTab === worker.name}
                        onClick={e => {
                          onClick({
                            ...node,
                            iconName,
                            class: node.class,
                            metadata: {
                              appName: worker.name
                            }
                          });
                          setActivetab(worker.name);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                          e.stopPropagation();
                        }}
                      >
                        <WorkerBadge>
                          <WorkerIcon />
                        </WorkerBadge>
                        <Tooltip
                          tooltip={worker.displayName}
                          delayDuration={1200}
                        >
                          <SelectText>{worker.displayName}</SelectText>
                        </Tooltip>
                      </SelectRow>
                    ))}
                  </Select>
                ) : null}
              </Column>
            ) : null}
          </Block>
        );
      })}

      <TitleHead tag="h4">
        {capitalizedServicesTitle}
        <BodyText size="medium" $color="granite">
          {`(${Object.keys(nodesServices).length})`}
        </BodyText>
      </TitleHead>

      {serviceNodes.map(k => {
        const node = nodesServices[k];
        node.name = k;

        const iconName = node?.type ? getServiceIcon(node).iconName : "router";
        return (
          <Block
            key={k}
            selected={activeTab === k}
            onClick={() => {
              onClick({
                ...node,
                iconName,
                class: node.class,
                metadata: {
                  appName: k
                }
              });
              setActivetab(k);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <BlockLeftLayout>
              <PlainServiceIcon
                service={node}
                iconName={iconName}
                alt={`${iconName}-icon`}
              />
              <Name>{node.name}</Name>
            </BlockLeftLayout>
          </Block>
        );
      })}
    </Layout>
  );
};

ServicesList.propTypes = {
  onClick: PropTypes.func,
  currentDeployment: PropTypes.object,
  theme: PropTypes.object,
  search: PropTypes.string,
  organizationId: PropTypes.string,
  projectId: PropTypes.string,
  selected: PropTypes.string,
  environmentId: PropTypes.string,
  autoscalingConfig: PropTypes.object
};

export default withTheme(ServicesList);
